import { CombinedGraphQLErrors } from '@apollo/client';
import type { useQuery } from '@apollo/client/react';
import { Box } from 'braid-design-system';
import { endOfHour } from 'date-fns/endOfHour';
import { sub } from 'date-fns/sub';
import { useCallback, useState, useRef } from 'react';

import type { useHirerState } from './components/HirerPicker/HirerPicker.tsx';
import type { useScopeState } from './components/ScopePicker/ScopePicker.tsx';
import { WebhookSubscriptionCard } from './components/WebhookSubscriptionCard/WebhookSubscriptionCard.tsx';
import { WEBHOOK_SUBSCRIPTIONS } from './queries.ts';

import type { useEventTypeState } from '#src/components/EventTypePicker/EventTypePicker.tsx';
import { InfiniteScrollingList } from '#src/components/InfiniteScrollingList/InfiniteScrollingList.tsx';
import {
  usePaginationDirectionState,
  type ProcessPageInfo,
} from '#src/components/PaginationDirectionPicker/PaginationDirectionPicker.tsx';
import { Query } from '#src/components/Query/Query.tsx';
import { useEnvironmentConfig } from '#src/hooks/environment.tsx';
import type {
  WebhookSubscriptionsQuery,
  WebhookSubscriptionsQueryVariables,
} from '#src/types/generated/graphql.ts';

export const SubscriptionListPanel = ({
  eventTypeState,
  scopeState,
  hirerState,
}: {
  eventTypeState: ReturnType<typeof useEventTypeState>;
  scopeState: ReturnType<typeof useScopeState>;
  hirerState: ReturnType<typeof useHirerState>;
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef(false);
  const { seekAnzSchemeId } = useEnvironmentConfig();

  // We don't modify this state at the moment but we utilise its helpers.
  const { firstOrLast, processPageInfo } =
    usePaginationDirectionState('FORWARDS');

  const { eventTypeCodes } = eventTypeState;
  const { hirerScopedIndicator } = scopeState;

  const { hirers } = hirerState;

  // We only show up to 30 days of requests so don't calculate deliverability
  // based on requests they can't see. Round up to the next hour to be nicer to
  // Apollo's cache
  const requestsSince = endOfHour(sub(new Date(), { days: 30 })).toISOString();

  return (
    <Query<WebhookSubscriptionsQuery, WebhookSubscriptionsQueryVariables>
      errorMessage="We couldn’t load your subscriptions."
      query={WEBHOOK_SUBSCRIPTIONS}
      variables={{
        filter: {
          eventTypeCodes,
          hirerIds: hirers?.map((hirer) => hirer.id.value),
          hirerScopedIndicator,
        },
        requestsSince,
        [firstOrLast]: 10,
        schemeId: seekAnzSchemeId,
      }}
      context={{ criticalErrorsOnlyTracking: true }}
      notifyOnNetworkStatusChange={false}
    >
      {({ data, fetchMore }) => (
        <SubscriptionList
          data={data}
          fetchMore={fetchMore}
          processPageInfo={processPageInfo}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          loadingRef={loadingRef}
        />
      )}
    </Query>
  );
};

interface SubscriptionListProps {
  data: WebhookSubscriptionsQuery;
  fetchMore: ReturnType<
    typeof useQuery<
      WebhookSubscriptionsQuery,
      WebhookSubscriptionsQueryVariables
    >
  >['fetchMore'];
  processPageInfo: ProcessPageInfo;
  errorMessage: string;
  setErrorMessage: (error: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  loadingRef: React.MutableRefObject<boolean>;
}

const SubscriptionList = (props: SubscriptionListProps) => {
  const {
    data,
    fetchMore,
    processPageInfo,
    errorMessage,
    setErrorMessage,
    isLoading,
    setIsLoading,
    loadingRef,
  } = props;

  const { edges, pageInfo } = data.webhookSubscriptions;
  const { shouldLoadMore, variables } = processPageInfo(pageInfo);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || errorMessage) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setErrorMessage('');

    try {
      await fetchMore({ variables });
    } catch (err) {
      if (CombinedGraphQLErrors.is(err)) {
        const errorString = String(err.errors[0].message);
        setErrorMessage(errorString);
      } else {
        setErrorMessage(String(err));
      }
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  }, [
    fetchMore,
    variables,
    errorMessage,
    setErrorMessage,
    setIsLoading,
    loadingRef,
  ]);

  return (
    <InfiniteScrollingList
      loadMore={loadMore}
      shouldLoadMore={shouldLoadMore && !errorMessage && !isLoading}
      isEmpty={edges.length === 0}
      subject="webhook subscriptions"
      spacing={false}
      items={edges.map((edge) => (
        <WebhookSubscriptionCard
          subscription={edge.node}
          key={edge.node.id.value}
        />
      ))}
      Wrapper={Box}
      errorMessage={errorMessage}
    />
  );
};
